
import React, { useCallback } from 'react';

import { openDialog } from '../../../base/dialog';
import { translate } from '../../../base/i18n';
import {
    Icon,
    IconChat,
    IconChatLock,
    IconChatUnlock,
    IconClose,
    PollIcon
} from '../../../base/icons';
import { isLocalParticipantModerator } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { getBrandingColorPalette } from '../../../base/styles';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { toggleChat } from '../../actions.web';

import DisablePrivateChatDialog from './DisablePrivateChatDialog';

type Props = {

    /**
     * Function to be called when pressing the close button.
     */
    onCancel: Function,

    /**
     * An optional class name.
     */
    className: string,

    /**
     * Whether the polls feature is enabled or not.
     */
    isPollsEnabled: boolean,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,

    /**
     * Whether the local participant is a moderator.
     */
    _isModerator: boolean,

    /**
     * Whether the private chat is disabled.
     */
    _isPrivateChatDisabled: boolean,

    /**
     * Action to open a dialog.
     */
    _openDialog: Function
};

/**
 * Custom header of the {@code ChatDialog}.
 *
 * @returns {React$Element<any>}
 */
function Header({
    onCancel,
    className,
    _isPollsTabFocused,
    _isPrivateChatDisabled,
    interfaceConfig,
    t,
    _isModerator,
    _openDialog
}: Props) {

    const onKeyPressHandler = useCallback(e => {
        if (onCancel && (e.key === ' ' || e.key === 'Enter')) {
            e.preventDefault();
            onCancel();
        }
    }, [ onCancel ]);

    const chatHeaderStyle = {
        background: getBrandingColorPalette(interfaceConfig)?.primary,
        borderRadius: '20px 20px 0 0'
    };

    const disablePrivateChat = useCallback(() => {
        _openDialog(DisablePrivateChatDialog);
    }, [ _openDialog ]);

    const chatHeaderH1 = { color: '#FFFFFF',
        letterSpacing: '0.63px',
        fontSize: '20px' };

    const securityIconStyle = { display: 'flex',
        alignItems: 'center',
        gap: '10px' };


    return (
        <div
            className = { className || 'chat-dialog-header' }
            role = 'heading'
            style = { chatHeaderStyle }>
            <div className = 'chat-header-child' >
                <Icon
                    fillColor = { interfaceConfig?.applyMeetingSettings ? `${getBrandingColorPalette(interfaceConfig)?.accent1}!important` : '#fff' }
                    size = { 27 }
                    src = { _isPollsTabFocused ? PollIcon : IconChat } />
                <h1
                    style = { chatHeaderH1 }>{_isPollsTabFocused ? t('chat.tabs.polls') : t('chat.tabs.chat')}</h1>
            </div>
            <div style = { securityIconStyle }>
                {_isPollsTabFocused
                    ? <Icon
                        ariaLabel = { t('toolbar.closeChat') }
                        onClick = { onCancel }
                        onKeyPress = { onKeyPressHandler }
                        role = 'button'
                        src = { IconClose }
                        tabIndex = { 0 } /> : <div style = { securityIconStyle }>{_isModerator && <Tooltip
                        content = { _isPrivateChatDisabled ? t('chat.enablePrivateChat') : t('chat.disablePrivateChat') }
                        position = 'bottom-right' >
                        <Icon
                            className = { _isPrivateChatDisabled ? 'private-chat-disabled' : 'private-chat-enabled' }
                            id = { _isPrivateChatDisabled ? 'chat-recipient-disabled' : '' }
                            onClick = { disablePrivateChat }
                            src = { _isPrivateChatDisabled ? IconChatLock : IconChatUnlock } />
                    </Tooltip> }<Icon
                        ariaLabel = { t('toolbar.closeChat') }
                        onClick = { onCancel }
                        onKeyPress = { onKeyPressHandler }
                        role = 'button'
                        src = { IconClose }
                        tabIndex = { 0 } /></div>}
            </div>
        </div>
    );
}

const mapStateToProps = state => {
    return {
        _isModerator: isLocalParticipantModerator(state),
        _isPrivateChatDisabled: state['features/chat']?.isPrivateChatDisabled || false
    };
};

const mapDispatchToProps = {
    onCancel: toggleChat,
    _openDialog: openDialog
};

export default translate(connect(mapStateToProps, mapDispatchToProps)(Header));

